// src/services/visitorTrackingServices.js

import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/visitor-tracking`;

/**
 * Retourne les headers d'authentification administrateur.
 *
 * Le token est récupéré à chaque appel pour éviter de conserver
 * une ancienne valeur après une connexion ou une déconnexion.
 */
function getAuthHeaders() {
  const token = localStorage.getItem("token");

  if (!token) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

function handleServiceError(message, error) {
  console.error(message, error);

  if (error?.response?.data) {
    console.error("Réponse API :", error.response.data);
  }

  throw error;
}

/* -------------------------------------------------------------------------- */
/* ROUTES PUBLIQUES                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Démarre ou réactive une session visiteur.
 *
 * @param {Object} sessionData
 * @returns {Promise<Object>}
 */
export const startVisitorSession = async (sessionData) => {
  try {
    const response = await axios.post(`${apiUrl}/start`, sessionData);

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors du démarrage de la session visiteur :",
      error,
    );
  }
};

/**
 * Enregistre une nouvelle page consultée.
 *
 * @param {Object} pageViewData
 * @returns {Promise<Object>}
 */
export const addVisitorPageView = async (pageViewData) => {
  try {
    const response = await axios.post(
      `${apiUrl}/page-view`,
      pageViewData,
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de l'enregistrement de la page vue :",
      error,
    );
  }
};

/**
 * Informe le serveur que le visiteur est toujours présent.
 *
 * @param {Object} heartbeatData
 * @returns {Promise<Object>}
 */
export const sendVisitorHeartbeat = async (heartbeatData) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/heartbeat`,
      heartbeatData,
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de l'envoi du heartbeat visiteur :",
      error,
    );
  }
};

/**
 * Termine une session visiteur.
 *
 * Cette méthode Axios peut être utilisée lorsqu'on termine proprement
 * une session depuis l'application.
 *
 * Pour un départ de page ou la fermeture du navigateur, il sera préférable
 * d'utiliser endVisitorSessionWithBeacon.
 *
 * @param {string} sessionToken
 * @returns {Promise<Object>}
 */
export const endVisitorSession = async (sessionToken) => {
  try {
    const response = await axios.post(`${apiUrl}/end`, {
      session_token: sessionToken,
    });

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la fermeture de la session visiteur :",
      error,
    );
  }
};

/**
 * Termine une session avec navigator.sendBeacon.
 *
 * Cette fonction est adaptée à beforeunload/pagehide, car le navigateur
 * peut interrompre une requête Axios lorsque la page est fermée.
 *
 * @param {string} sessionToken
 * @returns {boolean}
 */
export const endVisitorSessionWithBeacon = (sessionToken) => {
  if (!sessionToken || !navigator.sendBeacon) {
    return false;
  }

  const body = JSON.stringify({
    session_token: sessionToken,
  });

  const blob = new Blob([body], {
    type: "application/json",
  });

  return navigator.sendBeacon(`${apiUrl}/end`, blob);
};

/**
 * Enregistre une action liée à la popup commerciale.
 *
 * Actions possibles :
 * - displayed
 * - interacted
 * - clicked
 * - chat_opened
 * - callback_opened
 *
 * @param {Object} popupData
 * @returns {Promise<Object>}
 */
export const registerVisitorPopupEvent = async (popupData) => {
  try {
    const response = await axios.post(
      `${apiUrl}/popup-event`,
      popupData,
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de l'enregistrement de l'événement popup :",
      error,
    );
  }
};

/* -------------------------------------------------------------------------- */
/* ROUTES ADMINISTRATEUR                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Récupère les visiteurs actuellement en ligne.
 *
 * @returns {Promise<Object>}
 */
export const getLiveVisitors = async () => {
  try {
    const response = await axios.get(`${apiUrl}/live`, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération des visiteurs en direct :",
      error,
    );
  }
};

/**
 * Récupère l'historique paginé des sessions.
 *
 * Filtres disponibles :
 * - page
 * - source
 * - country_code
 * - status
 * - exclude_bots
 * - page de pagination
 * - limit
 *
 * @param {Object} params
 * @returns {Promise<Object>}
 */
export const getVisitorSessions = async (params = {}) => {
  try {
    const response = await axios.get(`${apiUrl}/sessions`, {
      headers: getAuthHeaders(),
      params,
    });

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération des sessions visiteurs :",
      error,
    );
  }
};

/**
 * Récupère le détail d'une session avec son parcours,
 * ses conversations et ses demandes de rappel.
 *
 * @param {number|string} sessionId
 * @returns {Promise<Object>}
 */
export const getVisitorSessionById = async (sessionId) => {
  try {
    if (!sessionId) {
      throw new Error("L'identifiant de la session est requis.");
    }

    const response = await axios.get(
      `${apiUrl}/sessions/${sessionId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération du détail de la session :",
      error,
    );
  }
};

/**
 * Récupère les statistiques globales du tracking.
 *
 * @returns {Promise<Object>}
 */
export const getVisitorTrackingStats = async () => {
  try {
    const response = await axios.get(`${apiUrl}/stats`, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération des statistiques visiteurs :",
      error,
    );
  }
};

/**
 * Tente de terminer la session lors de la fermeture de la page.
 *
 * fetch avec keepalive est généralement plus fiable qu'une requête Axios
 * lors de la fermeture ou du rechargement du navigateur.
 */
export const endVisitorSessionKeepalive = (sessionToken) => {
  if (!sessionToken) return;

  try {
    fetch(`${apiUrl}/end`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        session_token: sessionToken,
      }),
      keepalive: true,
    }).catch(() => {
      // Le serveur basculera de toute façon la session hors ligne
      // après le délai du heartbeat.
    });
  } catch (error) {
    console.error(
      "Erreur lors de la fermeture keepalive de la session :",
      error,
    );
  }
};

/**
 * Récupère le résumé global des alertes visiteurs.
 *
 * Cette route est utilisée dans tout l'espace administrateur,
 * pas uniquement dans la page VisitorCenter.
 */
export const getVisitorAdminSummary = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/admin-summary`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération du résumé visiteurs :",
      error,
    );
  }
};